import useEmblaCarousel from 'embla-carousel-react'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { buildOptimizedSrcSet } from 'src/components/Images/buildSrcSet'
import useLogger from 'src/context/LoggerProvider/useLogger'
import { IPhoto } from 'src/types/graphql'
import ga from 'src/utils/pixels/gtag'

// Only mounts on a click, so it must never be part of the listing page's
// initial JS. `ssr: false` is safe here precisely BECAUSE it is never rendered
// on first paint — the rule against `dynamic()` without a synchronous import
// applies to always-rendered above-the-fold children, which this is not.
const PhotoGalleryOverlay = dynamic(
  () => import('src/components/Images/PhotoGalleryOverlay'),
  { ssr: false }
)

type GallerySource = 'desktop' | 'mobile'

/** Fire `listing_gallery_lightbox_open` when the viewer opens the gallery.
 *  The `source` slice tells us whether the desktop grid or the mobile carousel
 *  drove the open. The matching `..._close` (with depth) is fired by
 *  `PhotoGalleryOverlay` itself. */
const fireLightboxOpen = (
  listingId: string,
  photoIndex: number,
  photoCount: number,
  source: GallerySource
) => {
  ga.event('listing_gallery_lightbox_open', {
    listing_id: listingId,
    photo_index: photoIndex,
    photo_count: photoCount,
    source
  })
}

// Tile placement classes per gallery size.
//
// LAYOUTS[count - 1][tileIndex] = [colClass, rowClass] — Tailwind v4
// arbitrary-CSS classes that compile to stylesheet rules.
//
// Why classes instead of inline style={{ gridColumn, gridRow }}: React 19's
// strict attribute comparison fires #418 on inline-style CSS shorthand
// collapse. SSR serializes `grid-column: 1 / 3; grid-row: 1 / 3` as TWO
// separate properties; the browser DOM normalizes them into the
// `grid-area: 1 / 1 / 3 / 3` shorthand. Same computed CSS, different
// attribute bytes → React 19 #418. Confirmed via at-error
// captureHydrationDiff on /renta-bodegas-comerciales/.../obrera/...
// (Edge 148 Desktop, UNINET Mexico City, 2026-05-29). Same family as
// Trigger C (MUI button padding shorthand). Tailwind arbitrary-value
// classes (`[grid-column:1/3]`) generate a stylesheet rule scoped via
// className — no inline style attribute, no SSR/CSR byte mismatch.
//
// String literals (not template-composed) so the Tailwind JIT scans them.
const LAYOUTS = [
  [['[grid-column:1/5]', '[grid-row:1/3]']],
  [
    ['[grid-column:1/3]', '[grid-row:1/3]'],
    ['[grid-column:3/5]', '[grid-row:1/3]']
  ],
  [
    ['[grid-column:1/3]', '[grid-row:1/3]'],
    ['[grid-column:3/4]', '[grid-row:1/3]'],
    ['[grid-column:4/5]', '[grid-row:1/3]']
  ],
  [
    ['[grid-column:1/3]', '[grid-row:1/3]'],
    ['[grid-column:3/4]', '[grid-row:1/3]'],
    ['[grid-column:4/5]', '[grid-row:1/2]'],
    ['[grid-column:4/5]', '[grid-row:2/3]']
  ],
  [
    ['[grid-column:1/3]', '[grid-row:1/3]'],
    ['[grid-column:3/4]', '[grid-row:1/2]'],
    ['[grid-column:3/4]', '[grid-row:2/3]'],
    ['[grid-column:4/5]', '[grid-row:1/2]'],
    ['[grid-column:4/5]', '[grid-row:2/3]']
  ]
]

/** Tiles the desktop grid can place — `LAYOUTS` tops out at five. */
const MAX_DESKTOP_TILES = 5

type IProps = {
  /** DOM id for the two gallery containers. Not an analytics key. */
  galleryId: string
  /**
   * Real `location.id`, used as the `listing_id` analytics param.
   *
   * Kept separate from `galleryId` on purpose. Until 2026-09-09 every gallery
   * event sent `listing_id: galleryId`, and `ImagesWrapper` hardcodes that to
   * the literal `'galley-details'` — so every listing in the app reported the
   * same constant and gallery engagement could not be joined to a listing, to
   * `view_item`, or to any conversion. Historical `listing_gallery_*` rows are
   * unusable per-listing for that reason.
   */
  listingId: string
  /** Shown in the full-screen gallery header, e.g. "Mini-Bodega · Centro". */
  galleryTitle: string
  images: IPhoto[]
  showDots?: boolean
  showPages?: boolean
  alt: string
}

function SimpleGallery({
  images,
  galleryId,
  listingId,
  galleryTitle,
  showDots,
  showPages,
  alt
}: IProps) {
  const logger = useLogger()
  const { t } = useTranslation('details')

  /**
   * Embla Carousel
   */
  const [emblaRef, emblaApi] = useEmblaCarousel({})
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  // Skip the initial 'select' fire (Embla emits one on mount). Only count
  // user-driven swipes / dot taps as `listing_gallery_carousel_advance`.
  const carouselSelectFiredRef = useRef(false)
  const onInit = useCallback(() => {
    if (emblaApi) setScrollSnaps(emblaApi.scrollSnapList())
  }, [emblaApi])
  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index)
    },
    [emblaApi]
  )
  const onSelect = useCallback(() => {
    if (!emblaApi) return
    const idx = emblaApi.selectedScrollSnap()
    setSelectedIndex(idx)
    if (carouselSelectFiredRef.current) {
      ga.event('listing_gallery_carousel_advance', {
        listing_id: listingId,
        to_index: idx
      })
    } else {
      carouselSelectFiredRef.current = true
    }
  }, [emblaApi, listingId])
  useEffect(() => {
    if (emblaApi) {
      onInit()
      emblaApi.on('reInit', onInit)
      emblaApi.on('select', onSelect)
    }
  }, [emblaApi, onInit, onSelect])

  /**
   * Full-screen gallery — opened on demand, never rendered on first paint.
   */
  const photoCount = images.length
  const [openedAt, setOpenedAt] = useState<{
    index: number
    source: GallerySource
  } | null>(null)

  const openGallery = useCallback(
    (index: number, source: GallerySource) => {
      fireLightboxOpen(listingId, index, photoCount, source)
      setOpenedAt({ index, source })
    },
    [listingId, photoCount]
  )

  const layout = LAYOUTS[Math.min(images.length, 5) - 1]

  return (
    <>
      {/* Desktop Gallery */}
      <div
        // Fixed height on purpose. A viewport-clamped height (300–500px) was
        // tried 2026-09-06 to lift the price card's CTA above the fold at
        // 1440×900 and reverted the same day: the side tiles dropped to
        // ~155px and the photos read as thumbnails, while desktop visitors
        // open the lightbox at 39% — the gallery is not the thing to shrink.
        // The CTA-under-the-fold problem is a price-card layout question.
        className="hidden h-[450px] w-full grid-cols-4 gap-3 py-2 md:grid lg:h-[500px]"
        id={galleryId + 'desktop'}
      >
        {images.map((image, index) => {
          const [colClass, rowClass] = layout[index] ?? ['', '']
          // Tile placement via Tailwind arbitrary-value classes instead of
          // inline style — see LAYOUTS comment for the React 19 #418 reason.
          //
          // Past the fifth tile the grid has nowhere to put a photo. These used
          // to be rendered as hidden anchors because PhotoSwipe built its slide
          // list from this container's anchors, so omitting them capped the
          // desktop lightbox at 5 photos. `PhotoGalleryOverlay` takes the whole
          // `images` array directly, so the hidden anchors are no longer needed.
          if (index >= MAX_DESKTOP_TILES) return null
          return (
            <a
              href={image.url ?? ''}
              key={galleryId + '-' + index}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                e.preventDefault()
                openGallery(index, 'desktop')
              }}
              className={`relative block size-full overflow-hidden rounded-xl transition-transform hover:scale-[1.02] ${colClass} ${rowClass}`}
            >
              {/* Plain <img> instead of <ImageWithFallback fill>. next/image's
                  `fill` mode injects a 7-property style block
                  (position:absolute;...color:transparent) plus our own
                  `style={{ objectFit: 'cover' }}` merges in. The combined inline
                  style block is the dominant byte-mismatch surface fueling
                  React 19 #418 from Googlebot + AdsBot crawls on listing pages.
                  Confirmed via at-error captureHydrationDiff on
                  /renta-bodegas-comerciales/santa-catarina/parque-200/...
                  (Googlebot Smartphone, 2026-05-31).

                  Trade-off: plain <img> loses the responsive srcset/AVIF
                  optimization from Vercel's image pipeline. For real-user
                  mobile traffic this is a bandwidth hit. ACCEPTED because (a)
                  listing-page #418 hurts SEO crawl efficiency on the surfaces
                  that matter most for organic traffic, (b) Vercel still serves
                  the photos through its CDN edge cache (just without
                  on-the-fly resize), (c) the index===0 photo is loaded eagerly
                  anyway so srcset wasn't deferring much. If mobile LCP
                  regresses, revisit with a manual srcset constructed from
                  `_next/image?url=...&w=X` parameters or a host-side resize. */}
              {/* JSX-prop order matters: `src` LAST. React 19 CSR's DOM
                  attribute setting puts `src` last (likely because setting
                  src triggers the image load — React defers it). SSR
                  serializes in JSX-prop order; if `src` is first in JSX,
                  SSR emits src-first while CSR re-orders to src-last,
                  producing the documented Phase 3 attribute-order drift
                  (confirmed on Chrome Desktop, 2026-06-01,
                  /renta-bodegas-comerciales/.../tultitlan-park). Putting
                  `src` last in JSX aligns SSR with CSR. We preserve <img>
                  (not background-image) to keep alt text for Google Image
                  Search indexability on SEO-critical listing photos.
                  eslint-disable for the @next/next/no-img-element rule
                  since plain <img> is the deliberate choice here. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                alt={alt ? `${alt} - Foto ${index + 1}` : `Foto ${index + 1}`}
                className="absolute inset-0 size-full object-cover"
                // loading="eager" is the browser default - omit for index===0
                // (would itself fire Trigger B). Only emit when non-default.
                loading={index === 0 ? undefined : 'lazy'}
                fetchPriority={index === 0 ? 'high' : undefined}
                // Lead tile spans ~half the 1280px container, side tiles a
                // quarter. Static strings → no SSR/CSR drift. Keep `srcSet`
                // then `src` as the LAST two props (React 19 CSR order).
                sizes={
                  index === 0
                    ? '(min-width: 900px) 50vw, 100vw'
                    : '(min-width: 900px) 25vw, 100vw'
                }
                onError={(e) => {
                  logger.error('Error loading img', e)
                  const img = e.currentTarget as HTMLImageElement
                  img.srcset = ''
                  img.src = '/images/empty.jpeg'
                }}
                srcSet={buildOptimizedSrcSet(image.url)}
                src={image.url ?? ''}
              />
              {index === MAX_DESKTOP_TILES - 1 &&
                images.length > MAX_DESKTOP_TILES && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      ga.event('listing_gallery_grid_more_click', {
                        listing_id: listingId,
                        photo_count: images.length
                      })
                      // Open on the first photo the grid could NOT show —
                      // that is what the badge is promising.
                      openGallery(MAX_DESKTOP_TILES, 'desktop')
                    }}
                    className="absolute right-4 bottom-4 flex items-center gap-1.5 rounded-lg bg-white-500/95 px-4 py-2 text-sm font-semibold text-black-500 shadow-lg backdrop-blur-sm transition-all hover:bg-white-500 hover:shadow-xl"
                  >
                    <i className="si si-images text-base" />
                    {/* Single text node, not `Ver {n} fotos` — the {n}
                      interpolation splits into text→expr→text and React emits
                      `<!-- -->` boundary comments (`Ver <!-- -->11<!-- --> fotos`)
                      that FB IAB + shopping crawlers strip → #418. See CLAUDE.md
                      "Trigger H". `t()` interpolates inside i18next and returns
                      one finished string, so this stays a single text node — do
                      NOT split the count back out into a JSX expression. */}
                    {t('SECTIONS.VIEW_PHOTOS', { count: images.length })}
                  </button>
                )}
            </a>
          )
        })}
      </div>

      {/* Mobile Carousel */}
      <div className="relative -mx-5 bg-grey-250 md:hidden">
        <div className="overflow-hidden" ref={emblaRef}>
          <div
            className="-ml-5 flex tap-highlight-transparent select-none"
            id={galleryId + 'mobile'}
          >
            {images.map((image, index) => (
              <a
                href={image.url ?? ''}
                key={galleryId + '-' + index}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  e.preventDefault()
                  openGallery(index, 'mobile')
                }}
                className="relative min-w-full bg-grey-300"
              >
                <div className="relative flex h-[240px] w-full items-center justify-center overflow-hidden sm:h-[280px]">
                  {/* Plain <img> with `src` LAST in JSX-prop order to match
                      React 19 CSR's DOM attribute setting order. Same Phase
                      3 fix as the desktop gallery above. */}
                  {}
                  {/* `sizes` + `srcSet` are static strings computed at
                      render — identical on SSR and CSR, so they don't
                      widen the #418 surface. JSX order matters: React 19
                      CSR sets `srcSet` then `src` LAST, so keep them as
                      the final two props in that order. See buildSrcSet.ts
                      for why this exists (mobile LCP). */}
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    alt={alt}
                    className="absolute inset-0 size-full object-cover"
                    loading={index === 0 ? undefined : 'lazy'}
                    fetchPriority={index === 0 ? 'high' : undefined}
                    sizes="100vw"
                    onError={(e) => {
                      const img = e.currentTarget as HTMLImageElement
                      // Clear srcset too — with candidates still present the
                      // browser keeps preferring them over the fallback src.
                      img.srcset = ''
                      img.src = '/images/empty.jpeg'
                    }}
                    srcSet={buildOptimizedSrcSet(image.url)}
                    src={image.url ?? ''}
                  />
                </div>
              </a>
            ))}
          </div>
        </div>

        {showDots && scrollSnaps.length > 1 && (
          <div className="absolute inset-x-0 bottom-4 z-10 flex items-center justify-center">
            {scrollSnaps.map((_, index) => (
              <button
                key={index}
                className={`mx-1 h-1.5 w-2 rounded-full transition-colors ${
                  index === selectedIndex ? 'bg-blue-500' : 'bg-blue-300'
                }`}
                onClick={() => scrollTo(index)}
              />
            ))}
          </div>
        )}

        {showPages &&
          (() => {
            const maxDigits = String(scrollSnaps.length).length
            const current = String(selectedIndex + 1).padStart(maxDigits, '0')
            const total = String(scrollSnaps.length).padStart(maxDigits, '0')
            // dangerouslySetInnerHTML to eliminate React boundary comments
            // (<!-- -->) between {current}{' '}<span>/</span> {total}. FB IAB
            // strips HTML comments before exposing the DOM to React →
            // hydration #418 on listing-detail pages. The current/total
            // values are digit strings from String(N).padStart(...) — safe
            // to embed verbatim, no escape needed. See CLAUDE.md "Trigger H".
            return (
              <div
                className="absolute right-4 bottom-4 z-10 font-display text-[13px] text-white-500 italic [text-shadow:0_1px_4px_rgba(0,0,0,0.55)] md:text-sm"
                dangerouslySetInnerHTML={{
                  __html: `${current} <span class="text-white-500/70 not-italic">/</span> ${total}`
                }}
              />
            )
          })()}
      </div>

      {openedAt && (
        <PhotoGalleryOverlay
          images={images}
          alt={alt}
          listingId={listingId}
          title={galleryTitle}
          initialIndex={openedAt.index}
          source={openedAt.source}
          onClose={() => setOpenedAt(null)}
        />
      )}
    </>
  )
}

export default React.memo(SimpleGallery)
